import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchCoverageBatch, coverageKey, batchEntryKey } from "./coverageSlice";
import { normalizeMarketLatLng } from "../config/market";

// Backend caps a batch at 60 ids; chunk defensively below that.
const CHUNK_SIZE = 50;

/**
 * useCoverageBatch — authoritative deliverability for a LIST of vendors at the
 * customer's browse location (one POST /api/coverage/batch instead of N GETs).
 *
 * Feeds the same redux `coverage.byKey` cache as the single-vendor useCoverage
 * hook, keyed by (vendorId, rounded lat/lng) — so a batch on /restaurants
 * pre-warms the shop-entry banner of whichever vendor the customer opens next,
 * and re-renders never refetch.
 *
 * Outlet cards pass `entries` of `{ vendorId, outletId }`: the verdict is then
 * measured from the OUTLET's own coordinates and radius and lands under the
 * outlet id, the same key useCoverage reads on that outlet's storefront. A
 * plain id in either list is the parent vendor itself.
 *
 * @param {object}   params
 * @param {string[]} [params.vendorIds]  vendor ids to check (deduped inside)
 * @param {Array<string|{vendorId:string, outletId?:string}>} [params.entries]
 * @param {boolean}  [params.enabled=true]
 * @param {string}   [params.vendorType]
 * @returns {{
 *   byVendorId: Object<string, {status,deliverable,distanceKm,radius,deliveryCoverage,coverageCountry,reason}>,
 *                            // keyed by vendor id, or by outlet id for an outlet entry
 *   hasLocation: boolean,
 *   isChecking: boolean,     // any requested vendor still loading
 * }}
 */
export default function useCoverageBatch({
  vendorIds = [],
  entries = [],
  enabled = true,
  vendorType,
} = {}) {
  const dispatch = useDispatch();
  const { latitude, longitude } = useSelector((state) => state.userLocation);
  const byKey = useSelector((state) => state.coverage.byKey);

  const coordinates = normalizeMarketLatLng(latitude, longitude);
  const latitudeUsed = coordinates?.lat;
  const longitudeUsed = coordinates?.lng;
  const hasLocation = Boolean(coordinates);

  // Stable, deduped entry map (order-insensitive) so effect deps don't thrash.
  // One entry per cache key: a parent listed twice is asked about once.
  const entryByKeyId = useMemo(() => {
    const map = new Map();
    [...vendorIds, ...entries].forEach((entry) => {
      if (!entry) return;
      if (typeof entry === "object" && !entry.vendorId) return;
      const id = batchEntryKey(entry);
      if (!map.has(id)) map.set(id, entry);
    });
    return map;
  }, [vendorIds, entries]);
  const idsKey = useMemo(
    () => [...entryByKeyId.keys()].sort().join(","),
    [entryByKeyId]
  );

  useEffect(() => {
    if (!enabled || !hasLocation || !idsKey) return;
    const ids = idsKey.split(",");
    // Only ask for vendors we don't already have (or aren't already fetching).
    const missing = ids
      .filter((id) => {
        const entry = byKey[coverageKey(id, latitudeUsed, longitudeUsed)];
        return !entry || (entry.status !== "succeeded" && entry.status !== "loading");
      })
      .map((id) => entryByKeyId.get(id));
    if (missing.length === 0) return;
    for (let i = 0; i < missing.length; i += CHUNK_SIZE) {
      dispatch(
        fetchCoverageBatch({
          vendorIds: missing.slice(i, i + CHUNK_SIZE),
          lat: latitudeUsed,
          lng: longitudeUsed,
          vendorType,
        })
      );
    }
    // byKey is intentionally read fresh on each (ids/location) change only —
    // including it in deps would re-run the effect on every fulfilled batch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, enabled, hasLocation, idsKey, latitudeUsed, longitudeUsed, vendorType]);

  const byVendorId = useMemo(() => {
    if (!hasLocation || !idsKey) return {};
    const out = {};
    idsKey.split(",").forEach((id) => {
      const entry = byKey[coverageKey(id, latitudeUsed, longitudeUsed)];
      if (entry) out[id] = entry;
    });
    return out;
  }, [byKey, hasLocation, idsKey, latitudeUsed, longitudeUsed]);

  const isChecking =
    hasLocation &&
    Boolean(idsKey) &&
    idsKey.split(",").some((id) => byVendorId[id]?.status === "loading" || !byVendorId[id]);

  return { byVendorId, hasLocation, isChecking };
}
